import axios from 'axios';
import { nanoid } from 'nanoid';
import Cookies from 'js-cookie';

import { CONFIG } from './config';
import {
  API_ERROR_TYPES,
  AUTH_METHODS,
  COOKIES,
  LOCAL_STORAGE_KEYS,
  SESSION_INFO_COOKIE_NAMES,
} from './constants';
import { getRefreshTokenFromCookie } from 'utils';

/**
 * Parses a Retry-After HTTP header value into seconds.
 *
 * @param {string|undefined} headerValue - Raw header value
 *
 * @returns {number|null} Seconds to wait, or null if not parseable
 */
export const parseRetryAfterHeader = (headerValue) => {
  const seconds = parseInt(headerValue, 10);
  return Number.isFinite(seconds) ? seconds : null;
};

/**
 * Checks whether an error or response represents an HTTP 429 rate-limit.
 *
 * @param {{ status?: number, type?: string }} errorResponse - Error or response shape
 *
 * @returns {boolean}
 */
export const isRateLimitError = (errorResponse) =>
  errorResponse?.status === 429 || errorResponse?.type === API_ERROR_TYPES.RATE_LIMIT;

/**
 * function to get asset urls from CDN
 *
 * @param {string} assetPath
 *
 * @returns {string} fullUrl
 */
export const getAssetURL = (assetPath) => `${CONFIG.CDN_BASEURL}${assetPath}`;

/**
 * function to get the a dash prefixed ENV for selecting the right domain from the hostname
 *
 * @returns {string}
 */
const getDomainEnvFromHostname = () => {
  const subdomain = window.location.hostname.split('.').shift();

  const envWithDashPrefixed = subdomain.replace(CONFIG.APP_NAME, '');

  return envWithDashPrefixed;
};

/**
 * function to get the right env
 *
 * @returns { 'dev' | 'staging' | 'production' | 'demo' | 'predemo' | 'experiment' | 'hotfix' }
 */
export const getAppEnv = () => {
  const envWithDashPrefixed = getDomainEnvFromHostname();

  if (envWithDashPrefixed) {
    if (envWithDashPrefixed === 'localhost') {
      // localhost will not have a prefix, we return the result as is
      return 'localhost';
    }
    return envWithDashPrefixed.slice(1);
  }

  return 'production';
};

/**
 * function to get the Platform URL
 *
 * @return {string}
 */
export const getPlatformURL = () =>
  CONFIG.ENV !== 'production'
    ? CONFIG.LOCAL_PLATFORM_URL
    : `https://platform${getDomainEnvFromHostname()}.integrtr.com`;

/**
 * @function
 *
 * redirect to login page url
 *
 * @param {string} [locationHref]
 */
export const redirectToLoginPage = (locationHref = window.location.href) => {
  // Locally, platform-ui is served directly by the UI5 dev server, which has no clean-URL
  // rewrite - only the literal /login.html resolves. Deployed environments serve the built
  // app via `serve`, which resolves /login to login.html automatically.
  const loginPath = CONFIG.ENV !== 'production' ? '/login.html' : '/login';

  window.location.replace(
    `${getPlatformURL()}${loginPath}?redirectTo=${encodeURIComponent(locationHref)}`
  );
};

/**
 * function to get auth token
 *
 */
export const getAuthToken = () => {
  const cookieToken = Cookies.get(COOKIES.BASIC_AUTH_TOKEN) || Cookies.get(COOKIES.SSO_AUTH_TOKEN);
  if (cookieToken) {
    return cookieToken;
  }

  return CONFIG.AUTH_TOKEN;
};

/**
 * function to get the headers for API calls
 *
 * @param {string} tenantId
 * @param {object} options
 * @param {boolean} options.tenantId
 * @param {string} options.authToken
 *
 * @throws If cookies cannot be found in production ENV then we redirect them to Home
 *
 * @returns {{ 'X-TenantId': string, 'Authorization': string }}
 */
const getHeaders = (
  tenantId,
  { isWorker = false, authToken = getAuthToken(), refreshToken } = {}
) => {
  const xRefreshToken = refreshToken ? { 'x-refresh-token': refreshToken } : {};

  return {
    ...(authToken && { Authorization: `Bearer ${authToken}` }),
    ...(Boolean(tenantId) && { 'X-TenantId': tenantId }),
    ...xRefreshToken,
  };
};

/**
 * function to get the API base URL based on the ENV values
 *
 * @return {string}
 */
export const getAPIBaseURL = () => {
  if (CONFIG.ENV !== 'production') {
    return CONFIG.API_BASE_URL;
  }

  const env = getDomainEnvFromHostname();

  return `https://platform${env}-api.integrtr.com`;
};

/**
 * Retrieves the base URL for the help documentation based on the current environment.
 * @returns {string} The base URL of the help documentation.
 */
export const getHelpBaseUrl = () => {
  const env = getAppEnv();

  if (env === 'localhost' || env === 'dev') {
    return 'https://help-staging.integrtr.com'; // we don't have dev DB in HELP, so we use staging help
  }
  if (env === 'staging') {
    return `https://help-${env}.integrtr.com`;
  }
  return 'https://help.integrtr.com';
};

/**
 * Constructs the URL for a specific help page based on the requested page name.
 * @param {string} requestedPage - The name of the help page to retrieve the link for.
 * @returns {string} The full URL of the requested help page.
 */
export const getHelpPageLink = (requestedPage) => {
  return `${getHelpBaseUrl() + requestedPage}`;
};

export const systemApiBase = '/app/system';
export const dataflowApiBase = '/app/dataflow';
export const billingApiBase = '/app/billing';
export const mdGenApiBase = '/app/mdgen';

const responseInterceptor = (response) => response.data;
const errorInterceptor = (error) => {
  if (axios.isCancel(error)) {
    throw error;
  }

  let errorResponse = { key: nanoid(10) };
  if (error.response && error.response.data) {
    errorResponse = {
      status: error.response.status,
      ...errorResponse,
      ...(typeof error.response.data === 'object'
        ? { ...error.response.data }
        : {
            errorDetails: error.response.data,
          }),
    };

    // our errorResponse shape doesn't carry headers; copy Retry-After (seconds)
    // so 429 handlers can surface the wait time
    if (error.response.status === 429) {
      const retryAfterSeconds = parseRetryAfterHeader(error.response.headers?.['retry-after']);
      if (retryAfterSeconds !== null) {
        errorResponse.retryAfterSeconds = retryAfterSeconds;
      }
    }
  }

  throw errorResponse;
};

// instance with no encoding/decoding
const rawCookies = Cookies.withConverter({
  read: (value) => value,
  write: (value) => value,
});

/**
 * Retrieves the value of a cookie by name.
 * @param {string} name - The name of the cookie.
 * @returns {string|null} The cookie value or null if not found.
 */
export function getCookie(name) {
  return rawCookies.get(name) || null;
}

/**
 * Sets a cookie with the given name, value, and options. Always uses path: '/'.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to set.
 */
export function setCookie(name, value) {
  rawCookies.set(name, value, { path: '/', domain: CONFIG.DOMAIN_NAME });
}

/**
 * Deletes all cookies accessible from the current path.
 */
export function deleteAllCookies() {
  Object.keys(rawCookies.get()).forEach((name) => {
    rawCookies.remove(name, { path: '/', domain: CONFIG.DOMAIN_NAME });
  });
}

/**
 * Gets and parses a JSON cookie value, returns null if not found or invalid.
 * @param {string} name - The name of the cookie.
 * @returns {object|null}
 */
export function getParsedCookie(name) {
  try {
    return JSON.parse(getCookie(name));
  } catch {
    return null;
  }
}

/**
 * Updates the sessionInfo cookie with the current time and notifies listeners to reset inactivity timers.
 */
export function resetInactivityTimerAndUpdateCookie() {
  const now = Date.now();
  const cookieName = getSessionInfoCookieName();
  const cookieObj = getParsedCookie(cookieName) || {};
  cookieObj.lastAppUsedTime = now;
  setCookie(cookieName, JSON.stringify(cookieObj));
  // Notify listeners (e.g., inactivity dialog) to reset their timers
  window.dispatchEvent(new CustomEvent('reset-inactivity-timer'));
}

/**
 * Attempts to refresh the auth token by calling the refresh endpoint.
 * If refresh fails, redirects to login. This is also an internal function.
 * @returns {Promise<string|null>} The new token or null if refresh failed.
 */
export async function refreshAuthToken() {
  try {
    const tenantId = getTenantIdFromUrl(window.location.href);
    const loginMethod = localStorage.getItem(LOCAL_STORAGE_KEYS.LOGIN_METHOD) || '';
    const refreshType = loginMethod === AUTH_METHODS.SSO ? 'cognito_token' : 'auth0_token';

    const refreshToken = getRefreshTokenFromCookie();
    const REFRESH_ENDPOINT = `${getAPIBaseURL()}/api/refresh?refresh_type=${refreshType}`;

    await axios.get(REFRESH_ENDPOINT, {
      withCredentials: true,
      headers: {
        ...getHeaders(tenantId, { refreshToken }),
      },
    });

    return Cookies.get(COOKIES.BASIC_AUTH_TOKEN) || Cookies.get(COOKIES.SSO_AUTH_TOKEN);
  } catch (error) {
    redirectToLoginPage();
    throw error;
  }
}

/**
 * Axios request interceptor to ensure every request has a valid Authorization header.
 * If no token is present, it attempts to refresh the token and set it in the header.
 * If refresh fails, it redirects to login.
 *
 */
async function handleAuthTokenInterceptor(config) {
  let token = getAuthToken();
  if (!token) {
    token = await refreshAuthToken();
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

/**
 * @param {string} [tenantId]
 *
 * @returns [AxiosInstance]
 */
export const getServiceInstance = (
  tenantId,
  { isWorker = false, authToken, refreshToken } = {}
) => {
  // Inactivity resetter will fail in service workers
  if (typeof document !== 'undefined') {
    resetInactivityTimerAndUpdateCookie();
  }
  const serviceInstance = axios.create({
    withCredentials: true,
    headers: {
      ...getHeaders(tenantId, { isWorker, authToken, refreshToken }),
      'Content-Type': 'application/json',
    },
    baseURL: `${getAPIBaseURL()}/api`,
  });

  // attach unique error keys to each error so that useNotifyError hook
  // can identify new errors and show them
  serviceInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

  // Only add the request interceptor if not in a worker
  if (!isWorker) {
    serviceInstance.interceptors.request.use(handleAuthTokenInterceptor, (error) =>
      Promise.reject(error)
    );
  }

  return serviceInstance;
};

/**
 * Function returns all the users belonging to the provided tenant
 *
 * @param {string} tenantId
 */
export function getUsersByTenantId(tenantId) {
  return getServiceInstance(tenantId).get('/tenant/users');
}

/**
 * Returns the correct session info cookie name for the current environment.
 * @returns {string} The session info cookie name.
 */
export function getSessionInfoCookieName() {
  const env = getAppEnv();
  const envMap = {
    localhost: SESSION_INFO_COOKIE_NAMES.LOCAL,
    dev: SESSION_INFO_COOKIE_NAMES.DEV,
    staging: SESSION_INFO_COOKIE_NAMES.STAGING,
    production: SESSION_INFO_COOKIE_NAMES.PROD,
    experiment: SESSION_INFO_COOKIE_NAMES.EXPT,
  };
  return envMap[env] || SESSION_INFO_COOKIE_NAMES.PROD;
}

/**
 * Extracts the tenant ID from a URL, where the tenant ID is the string after 't/' and before '/connection'.
 * @param {string} url - The URL to extract the tenant ID from.
 * @returns {string|null} The tenant ID if found, otherwise null.
 */
export function getTenantIdFromUrl(url) {
  const match = url.match(/\/t\/([^/]+)(?:\/|$)/);
  return match ? match[1] : null;
}
